import type {
  AnalyticsEventProperties,
  MicrostepAction,
  MicrostepKind,
  MicrostepProperties,
  MicrostepSurface,
} from "@healthos/analytics";
import type { NextActionPayload } from "@healthos/schemas";

type MomentData = NonNullable<NextActionPayload["moment"]>;

const MICROSTEP_KINDS: ReadonlySet<string> = new Set<MicrostepKind>([
  "reset",
  "guide",
  "story",
  "recipe",
  "quiet_check",
  "reinforcement",
  "capture",
  "moment",
  "tour",
  "comprehension",
  "brief",
]);

function microstepKind(kind: unknown): MicrostepKind {
  return typeof kind === "string" && MICROSTEP_KINDS.has(kind)
    ? (kind as MicrostepKind)
    : "unknown";
}

/** Day Zero cards report through the dayZero* events, never as microsteps. */
export function isDayZeroMicrostep(action: NextActionPayload): boolean {
  return action.kind === "tour" || action.kind === "comprehension";
}

/**
 * The properties every microstep event carries. Ids, copy, links and the
 * readings the card cites stay out: only the kind, tier, pillar and rule family.
 */
export function microstepProperties(
  action: NextActionPayload,
  surface: MicrostepSurface,
): MicrostepProperties {
  return {
    microstep_kind: microstepKind(action.kind),
    microstep_surface: surface,
    ...(action.tier ? { microstep_tier: action.tier } : {}),
    ...(action.category ? { microstep_pillar: action.category } : {}),
    ...(action.moment?.capability ? { microstep_rule_family: action.moment.capability } : {}),
  };
}

function actionForRoute(url: string): MicrostepAction {
  const path = url.split(/[?#]/)[0] ?? "";
  if (/^\/(challenges|steps)(\/|$)/.test(path)) return "challenges";
  if (/^\/community(\/|$)/.test(path)) return "community";
  return "navigate";
}

/** What a moment's primary tap did, read from where its handoff took the member. */
export function momentPrimaryAction(
  moment: MomentData | undefined,
  { openedLog = false }: { openedLog?: boolean } = {},
): MicrostepAction {
  if (moment?.handoff === "reset") {
    // Without a named reset the handoff lands on the library shelf.
    const named = moment.recommendedReset || moment.handoffUrl?.includes("content=");
    if (named) return "play_reset";
    return moment.handoffUrl ? "library" : "complete";
  }
  if (moment?.recommendedEpisode) return "play_episode";
  if (moment?.handoff === "calendar") return "calendar";
  if (moment?.handoffUrl) return actionForRoute(moment.handoffUrl);
  if (moment?.handoff === "connection") return "community";
  return openedLog ? "log_entry" : "complete";
}

/** What a non-moment card's primary tap did. */
export function cardPrimaryAction(kind: NextActionPayload["kind"]): MicrostepAction {
  switch (kind) {
    case "reset":
    case "guide":
      return "play_reset";
    case "story":
    case "recipe":
      return "open_content";
    case "capture":
      return "log_entry";
    case "brief":
      return "brief";
    case "tour":
      return "tour";
    default:
      return "complete";
  }
}

/** A broadcast is not a NextActionPayload, so it carries the kind and surface only. */
export function broadcastMicrostepProperties(): MicrostepProperties {
  return { microstep_kind: "moment", microstep_surface: "broadcast" };
}

/**
 * Push data is forwarded whole by the service worker; only the kind and the
 * rule leave it. Null for any push that is not a microstep.
 */
export function pushOpenedProperties(
  data: Record<string, unknown> | undefined,
): AnalyticsEventProperties["microstepPushOpened"] | null {
  const kind = microstepKind(data?.kind);
  if (kind === "unknown") return null;
  const rule = data?.rule;
  return {
    microstep_kind: kind,
    ...(typeof rule === "string" && rule.trim() ? { microstep_rule: rule } : {}),
  };
}
